import { AssetConstants, ErrorConstants, HttpConstants } from 'src/constants';
import { VALIDATION_CODES } from 'src/constants/validationCodes';
import { ValidationError } from 'src/errors/CustomErrors';
import { logger } from 'src/logger';

import { AspectRatio, AssetMetadataSpecs, DimensionSpecs } from './Types';

/**
 * Validates width and height
 *
 * @param width - The width to validate
 * @param height - The height to validate
 * @param specs - Dimension specifications to validate against
 * @throws {ValidationError} When dimensions don't meet specifications
 * @returns {boolean} True if validation passes
 */
export const validateDimensions = (
  width: number,
  height: number,
  specs: DimensionSpecs,
): boolean => {
  const dimensions = [
    {
      type: 'Width',
      value: width,
      min: specs.minResolution?.width,
      max: specs.maxResolution?.width,
    },
    {
      type: 'Height',
      value: height,
      min: specs.minResolution?.height,
      max: specs.maxResolution?.height,
    },
  ];

  for (const dim of dimensions) {
    if (dim.min && dim.value < dim.min)
      throw new ValidationError(
        `${dim.type} ${dim.value}px is less than minimum ${dim.min}px`,
        VALIDATION_CODES.DIMENSIONS_TOO_SMALL,
      );
    if (dim.max && dim.value > dim.max)
      throw new ValidationError(
        `${dim.type} ${dim.value}px is greater than maximum ${dim.max}px`,
        VALIDATION_CODES.DIMENSIONS_TOO_LARGE,
      );
  }
  return true;
};

/**
 * Validates the aspect ratio of dimensions
 *
 * @param width - The width to validate
 * @param height - The height to validate
 * @param aspectRatio - aspect ratio to validate against
 * @param aspectRatioTolerance - Tolerance for aspect ratio validation
 * @returns true if validation passes, throws ValidationError otherwise
 */
export const validateAspectRatio = (
  width: number,
  height: number,
  aspectRatio: AspectRatio,
  aspectRatioTolerance?: number,
): boolean => {
  if (height <= 0 || width <= 0) {
    throw new ValidationError('Dimensions cannot be zero or negative');
  }
  if (aspectRatio.width <= 0 || aspectRatio.height <= 0) {
    throw new ValidationError('Dimensions cannot be zero or negative');
  }
  const expectedRatio = aspectRatio.width / aspectRatio.height;
  const tolerance = aspectRatioTolerance ?? AssetConstants.DEFAULT_ASPECT_RATIO_TOLERANCE;
  const actualRatio = width / height;

  if (Math.abs(actualRatio - expectedRatio) > tolerance) {
    throw new ValidationError(
      `${ErrorConstants.MESSAGES.INVALID_ASPECT_RATIO} ${aspectRatio.width}:${aspectRatio.height}`,
      VALIDATION_CODES.WRONG_ASPECT_RATIO,
    );
  }

  return true;
};

/**
 * Validates content type against allowed and disallowed types
 *
 * @param contentType - The content type to validate
 * @param specs - Specifications containing allowed and disallowed types
 * @throws {ValidationError} When content type is null or not in allowed types
 */
const validateContentType = (contentType: string | null, specs: AssetMetadataSpecs): void => {
  if (contentType === null) {
    throw new ValidationError(
      ErrorConstants.MESSAGES.CONTENT_TYPE_HEADER_MISSING,
      VALIDATION_CODES.INVALID_FILE_TYPE,
    );
  }

  const isAllowed = contentType && specs.allowedTypes.includes(contentType.toLowerCase());
  if (!isAllowed) {
    throw new ValidationError(
      `Content type '${contentType}' is not allowed. Allowed types: ${specs.allowedTypes.join(', ')}`,
      VALIDATION_CODES.INVALID_FILE_TYPE,
    );
  }
};

/**
 * Validates file size against maximum size constraints
 *
 * @param contentLength - The file size in bytes
 * @param specs - Specifications containing min and max size
 * @throws {ValidationError} When content length is invalid or exceeds maximum size
 */
const validateFileSize = (contentLength: string | null, specs: AssetMetadataSpecs): void => {
  if (!contentLength) {
    throw new ValidationError(ErrorConstants.MESSAGES.CONTENT_LENGTH_HEADER_MISSING);
  }
  const contentSize = parseInt(contentLength, HttpConstants.PARSE.RADIX);

  if (isNaN(contentSize)) {
    throw new ValidationError('Content-Length is not a valid number');
  }

  if (contentSize > specs.maxSize) {
    const maxSizeKB = Math.round(specs.maxSize / 1024);
    const actualSizeKB = Math.round(contentSize / 1024);

    throw new ValidationError(
      `File size (${actualSizeKB} KB) exceeds the maximum allowed size of ${maxSizeKB} KB`,
      VALIDATION_CODES.FILE_TOO_LARGE,
    );
  }
};

/**
 * Validates asset metadata (content-type and file size) from headers
 *
 * @param headers - Headers object containing content-type and content-length
 * @param specs - Specifications for validation
 * @throws {ValidationError} When headers are missing or invalid, or when content type or size validation fails
 */
export const validateAssetMetadata = (headers: Headers, specs: AssetMetadataSpecs): void => {
  logger.info(`Validating asset metadata`);
  validateContentType(headers.get(HttpConstants.HEADERS.CONTENT_TYPE), specs);
  validateFileSize(headers.get(HttpConstants.HEADERS.CONTENT_LENGTH), specs);
};
